import { find } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import {
  useCategoriesCount,
  useCategoriesCountV2,
} from '../../../../hooks/useCategoriesCount';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { ChipCategory, GetCategoryCountProps } from './categoryCountTypes';
import {
  getPayloadWithRecriprocityId,
  groupSpecialtyRollupCodes,
} from './searchUtils';
import {
  compareAndReturn,
  getModifiedPayloadForCategoriesCount,
} from './utils';

export type UseParallelSearchResults = {
  isNameResultsLoaded: boolean;
  isSpecialityResultsLoaded: boolean;
  nameResults: ChipCategory[];
  specialityResults: ChipCategory[];
  corelationIds: string;
};

export const useParallelSearchResultsCategoriesCount: (
  customParams: any,
  onCategoryCountLoaded?: (data: any) => void
) => [
  results: UseParallelSearchResults,
  getCategoryCountResults: (params: GetCategoryCountProps) => any
] = (customParams: any, onCategoryCountLoaded?: (data: any) => void) => {
  const isNameLoaded = useRef(false);
  const isSpecialityLoaded = useRef(false);
  const shouldLoadName = useRef(false);
  const shouldLoadSpeciality = useRef(false);

  const nameResultsRef = useRef([]);
  const specialtyRef = useRef([]);

  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedAOECodesActive = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.GENDER_AFFIRMING_AOE_CODES,
  })?.active;

  const [enableSearchResultsV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
  ]);
  const allConfigs = useLagoon('config')();
  const [enableAoeMedical, enableCategoryCountV2] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AOE_MEDICAL,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CATEGORY_COUNT_V2_DO_NOT_USE,
  ]);

  const isVirtualCareMedicalUnetRailRoadSuggestionSuppressed =
    returnSuppressFlag(
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
        .SUPPRESS_MEDICAL_VIRTUAL_CARE_UNET_RR_SUGGESTIONS
    );

  const hbpbCodes =
    allConfigs
      ?.find((row) => row.key === 'VISION_NON_UHC_ICARE')
      ?.value?.split(',') || [];

  const [nameCategoryCount, getNameResults] = useCategoriesCount({
    onCompleted: (result: any) => {
      const resultFromChipCategoriesCount = enableCategoryCountV2
        ? result?.data?.getChipCategoriesCountV2
        : result?.data?.getChipCategoriesCount;
      const nameResults = shouldLoadName.current
        ? resultFromChipCategoriesCount || []
        : [];
      nameResultsRef.current = nameResults;
      isNameLoaded.current = true;
      if (isSpecialityLoaded.current) {
        const specialityResults = specialtyRef.current;
        onCategoryCountLoaded?.({ nameResults, specialityResults });
      }
    },
  });
  const [nameCategoryCountV2, getNameResultsV2] = useCategoriesCountV2({
    onCompleted: (result: any) => {
      const resultFromChipCategoriesCount =
        result?.data?.getChipCategoriesCountV2;
      const nameResults = shouldLoadName.current
        ? resultFromChipCategoriesCount || []
        : [];
      nameResultsRef.current = nameResults;
      isNameLoaded.current = true;
      if (isSpecialityLoaded.current) {
        const specialityResults = specialtyRef.current;
        onCategoryCountLoaded?.({ nameResults, specialityResults });
      }
    },
  });
  const [specialityCategoryCount, getSpecialityCategoryCount] =
    useCategoriesCount({
      onCompleted: (result: any) => {
        const resultFromChipCategoriesCount =
          result?.data?.getChipCategoriesCount;
        const specialityResults = shouldLoadSpeciality.current
          ? resultFromChipCategoriesCount || []
          : [];
        specialtyRef.current = specialityResults;

        isSpecialityLoaded.current = true;

        if (isNameLoaded.current) {
          const nameResults = nameResultsRef.current;
          onCategoryCountLoaded?.({ nameResults, specialityResults });
        }
      },
    });

  const [specialityCategoryCountV2, getSpecialityCategoryCountV2] =
    useCategoriesCountV2({
      onCompleted: (result: any) => {
        const resultFromChipCategoriesCount =
          result?.data?.getChipCategoriesCountV2;
        const specialityResults = shouldLoadSpeciality.current
          ? resultFromChipCategoriesCount || []
          : [];
        specialtyRef.current = specialityResults;

        isSpecialityLoaded.current = true;

        if (isNameLoaded.current) {
          const nameResults = nameResultsRef.current;
          onCategoryCountLoaded?.({ nameResults, specialityResults });
        }
      },
    });
  const [results, setResults] = useState({
    isNameResultsLoaded: false,
    isSpecialityResultsLoaded: false,
    nameResults: [],
    specialityResults: [],
    corelationIds: '',
  });

  const {
    data: nameChipCategoryData,
    corelationId: nameCategoryCountCoRelationId,
  } = enableCategoryCountV2 ? nameCategoryCountV2 : nameCategoryCount;

  const {
    data: specialityChipCategoryData,
    corelationId: specialityCategoryCountCoRelationId,
  } = enableCategoryCountV2
    ? specialityCategoryCountV2
    : specialityCategoryCount;

  const getCategoryCountResults = useCallback(
    (passedParams: GetCategoryCountProps) => {
      const {
        currentMember,
        onlineRetailers,
        featureFlags,
        pseudoVerticalsDetails,
        pseudoVerticals,
        includeSpecialityRollupCodes,
        searchType,
        uesTranslation,
        coverageTypes,
        keyword,
        search,
        searchTerm,
        isMixedSuggestions,
        isUserTextSearch,
        behavioralFacilityProgram,
        pseudoRollupCode,
        preferredFacilities,
        active,
        ...restParams
      } = passedParams;
      const params = enableSearchResultsV2 ? passedParams : customParams || {};
      isNameLoaded.current = false;
      isSpecialityLoaded.current = false;

      const payloadWithRecriprocityId = getPayloadWithRecriprocityId({
        currentMember,
        onlineRetailers,
        featureFlags,
        pseudoVerticals,
        pseudoVerticalsDetails,
        hbpbCodes,
        isVirtualCareMedicalUnetRailRoadSuggestionSuppressed,
        active,
        suppressedAOECodesActive,
      });

      const getNameSearchResults = () => {
        const namesPayload = getModifiedPayloadForCategoriesCount(
          payloadWithRecriprocityId,
          undefined,
          uesTranslation,
          search,
          keyword,
          pseudoVerticalsDetails?.isPseudoRollupSearch,
          isUserTextSearch,
          coverageTypes,
          false,
          [],
          false,
          '',
          enableAoeMedical
        );

        enableCategoryCountV2
          ? getNameResultsV2({
              ...params,
              payloadwithName: namesPayload,
              searchTerm: compareAndReturn(search, searchTerm),
              ...restParams,
            })
          : getNameResults({
              ...params,
              payloadwithName: namesPayload,
              searchTerm: compareAndReturn(search, searchTerm),
              ...restParams,
            });
      };

      const getSpecialityCategoryCountResults = () => {
        const codeByGroup = groupSpecialtyRollupCodes(
          includeSpecialityRollupCodes,
          searchType,
          true
        );

        const {
          isConditionSearch,
          typeaheadAoeCodes = [],
          enableBHAoeTypeahead,
        } = params;

        const specialityPayload = getModifiedPayloadForCategoriesCount(
          payloadWithRecriprocityId,
          codeByGroup,
          uesTranslation,
          !isUserTextSearch ? search : '',
          keyword,
          pseudoVerticalsDetails?.isPseudoRollupSearch,
          isUserTextSearch,
          coverageTypes,
          isConditionSearch,
          typeaheadAoeCodes,
          enableBHAoeTypeahead,
          behavioralFacilityProgram,
          enableAoeMedical
        );

        const paramsForCount = {
          ...params,
          payloadwithName: specialityPayload,
          searchTerm: compareAndReturn(search, searchTerm),
          isUserTextSearch,
          behavioralFacilityProgram,
          ...(pseudoRollupCode ? { pseudoRollupCode } : {}),
          ...(preferredFacilities ? { preferredFacilities } : {}),
          ...restParams,
          ...(enableSearchResultsV2
            ? { search, includeSpecialityRollupCodes, searchType, keyword }
            : {}),
        };
        enableCategoryCountV2
          ? getSpecialityCategoryCountV2(paramsForCount)
          : getSpecialityCategoryCount(paramsForCount);
      };

      const makeSpecialitySearch =
        searchType === Constants.SEARCH_TYPES.SPECIALTY ||
        pseudoVerticalsDetails?.isPseudoRollupSearch ||
        includeSpecialityRollupCodes?.length > 0 ||
        params.isConditionSearch ||
        params.typeaheadAoeCodes?.length > 0 ||
        params.gender?.length > 0 ||
        params.virtualCare ||
        params.acceptsEap;
      const makeNameSearch =
        (isMixedSuggestions || searchType === Constants.SEARCH_TYPES.NAME) &&
        !!search;

      isNameLoaded.current = !makeNameSearch;
      shouldLoadName.current = makeNameSearch;
      if (makeNameSearch) {
        getNameSearchResults();
      }

      isSpecialityLoaded.current = !makeSpecialitySearch;
      shouldLoadSpeciality.current = makeSpecialitySearch;
      if (makeSpecialitySearch) {
        getSpecialityCategoryCountResults();
      }
    },
    [
      getNameResults,
      getNameResultsV2,
      getSpecialityCategoryCount,
      getSpecialityCategoryCountV2,
    ]
  );
  const nameResultsData = enableCategoryCountV2
    ? nameChipCategoryData?.getChipCategoriesCountV2
    : nameChipCategoryData?.getChipCategoriesCount;
  const specialityResultsData = enableCategoryCountV2
    ? specialityChipCategoryData?.getChipCategoriesCountV2
    : specialityChipCategoryData?.getChipCategoriesCount;
  useEffect(() => {
    setResults({
      isNameResultsLoaded: isNameLoaded.current,
      isSpecialityResultsLoaded: isSpecialityLoaded.current,
      nameResults: shouldLoadName.current ? nameResultsData || [] : [],
      specialityResults: shouldLoadSpeciality.current
        ? specialityResultsData || []
        : [],
      corelationIds: `${
        shouldLoadName.current ? nameCategoryCountCoRelationId : ''
      }-${
        shouldLoadSpeciality.current ? specialityCategoryCountCoRelationId : ''
      }`,
    });
  }, [
    JSON.stringify(nameResultsData),
    JSON.stringify(specialityResultsData),
    specialityCategoryCountCoRelationId,
    nameCategoryCountCoRelationId,
    shouldLoadName.current,
    shouldLoadSpeciality.current,
    isNameLoaded.current,
    isSpecialityLoaded.current,
  ]);
  return [results, getCategoryCountResults];
};
